import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  CalendarClock, CalendarPlus, ChevronLeft, ChevronRight, Loader2, Mail, Pencil, Plus, Search, Trash2, X,
} from 'lucide-react';
import { PageHeader } from '../../../components/PageHeader';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Input } from '../../../components/Input';
import { EmptyState } from '../../../components/EmptyState';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/Popover';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/Dialog';
import { cn } from '../../../lib/utils';
import { useShiftStore, type ShiftAssignment, type ShiftType } from '../../../store/shiftStore';
import { useEmployeeStore } from '../../../store/employeeStore';
import { useRbacStore } from '../../../store/rbacStore';
import { ShiftTypeDialog } from './ShiftTypeDialog';
import { BulkAssignDialog, type RosterEmployee } from './BulkAssignDialog';
import {
  WEEKDAYS, addDays, formatHours, formatRange, fromKey, startOfWeek, todayKey, weekDays,
} from './rosterDates';

/** Weekly hours above this are flagged as overtime on the roster. */
const STANDARD_WEEKLY_HOURS = 48;

/* ───────────────────────────── shared pieces ─────────────────────────── */

function ShiftChip({ name, color, start, end, className }: {
  name: string; color: string | null; start?: string | null; end?: string | null; className?: string;
}) {
  return (
    <span
      className={cn('flex w-full min-w-0 flex-col rounded-md border-l-4 bg-secondary px-2 py-1 text-left', className)}
      style={{ borderLeftColor: color || 'hsl(var(--muted-foreground))' }}
    >
      <span className="truncate text-xs font-semibold text-foreground">{name}</span>
      {start && end && <span className="truncate text-xs text-muted-foreground tabular-nums">{start}–{end}</span>}
    </span>
  );
}

function WeekNav({ monday, onChange }: { monday: string; onChange: (monday: string) => void }) {
  const isThisWeek = monday === startOfWeek(todayKey());
  return (
    <div className="flex items-center gap-1">
      <Button variant="secondary" size="icon-sm" aria-label="Previous week" onClick={() => onChange(addDays(monday, -7))}>
        <ChevronLeft />
      </Button>
      <span className="min-w-40 text-center text-sm font-medium text-foreground tabular-nums">
        {formatRange(monday, addDays(monday, 6))}
      </span>
      <Button variant="secondary" size="icon-sm" aria-label="Next week" onClick={() => onChange(addDays(monday, 7))}>
        <ChevronRight />
      </Button>
      {!isThisWeek && (
        <Button variant="ghost" size="sm" onClick={() => onChange(startOfWeek(todayKey()))}>This week</Button>
      )}
    </div>
  );
}

const dayHeader = (key: string) => fromKey(key).getDate();

/* ────────────────────────────── roster cell ──────────────────────────── */

function RosterCell({ employeeId, day, assignment, shifts, isToday }: {
  employeeId: string;
  day: string;
  assignment?: ShiftAssignment;
  shifts: ShiftType[];
  isToday: boolean;
}) {
  const { assignShift } = useShiftStore();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = async (shiftTypeId: string | null) => {
    setOpen(false);
    if ((assignment?.shiftTypeId ?? null) === shiftTypeId) return;
    setSaving(true);
    try {
      await assignShift(employeeId, day, shiftTypeId);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <td className={cn('p-1 align-top', isToday && 'bg-primary/5')}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={saving}
            aria-label={assignment ? `${assignment.shiftName} on ${day}, change` : `Assign a shift on ${day}`}
            className="group flex h-12 w-full items-stretch rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            {saving ? (
              <span className="flex w-full items-center justify-center"><Loader2 className="size-4 animate-spin text-muted-foreground" /></span>
            ) : assignment ? (
              <ShiftChip
                name={assignment.shiftName}
                color={assignment.color}
                start={assignment.startTime}
                end={assignment.endTime}
                className="group-hover:ring-1 group-hover:ring-border-strong"
              />
            ) : (
              <span className="flex w-full items-center justify-center rounded-md border border-dashed border-transparent text-muted-foreground/50 group-hover:border-border group-hover:text-muted-foreground">
                <Plus className="size-4" />
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-60 p-1">
          <p className="px-2 pb-1 pt-1.5 text-xs text-muted-foreground">
            {fromKey(day).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' })}
          </p>
          {shifts.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => set(s.id)}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-secondary',
                assignment?.shiftTypeId === s.id && 'bg-secondary font-medium'
              )}
            >
              <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
              <span className="flex-1 truncate">{s.name}</span>
              <span className="text-xs text-muted-foreground tabular-nums">{s.startTime}–{s.endTime}</span>
            </button>
          ))}
          {assignment && (
            <>
              <div className="my-1 h-px bg-border" />
              <button
                type="button"
                onClick={() => set(null)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-destructive-strong hover:bg-destructive/10"
              >
                <X className="size-4" /> Clear this day
              </button>
            </>
          )}
        </PopoverContent>
      </Popover>
    </td>
  );
}

/* ─────────────────────────── shift type legend ───────────────────────── */

function ShiftTypeList({ shifts, canManage, onEdit, onDelete }: {
  shifts: ShiftType[];
  canManage: boolean;
  onEdit: (s: ShiftType) => void;
  onDelete: (s: ShiftType) => void;
}) {
  if (shifts.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {canManage ? 'No shift types yet. Create one to start building the roster.' : 'No shift types have been set up yet.'}
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-2">
      {shifts.map((s) => (
        <div key={s.id} className="group flex items-center gap-2 rounded-lg border border-border bg-card py-1.5 pl-3 pr-1.5 text-sm">
          <span className="size-2.5 rounded-full" style={{ backgroundColor: s.color }} />
          <span className="font-medium text-foreground">{s.name}</span>
          <span className="text-muted-foreground tabular-nums">{s.startTime}–{s.endTime} · {formatHours(s.hours)}</span>
          {canManage && (
            <span className="ml-1 flex items-center">
              <Button variant="ghost" size="icon-sm" className="size-7" aria-label={`Edit ${s.name}`} onClick={() => onEdit(s)}>
                <Pencil className="size-3.5" />
              </Button>
              <Button variant="ghost" size="icon-sm" className="size-7" aria-label={`Delete ${s.name}`} onClick={() => onDelete(s)}>
                <Trash2 className="size-3.5" />
              </Button>
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/* ──────────────────────────── employee's own view ────────────────────── */

function MyShifts({ monday, setMonday }: { monday: string; setMonday: (m: string) => void }) {
  const { shifts, assignments, swaps, isLoading, error } = useShiftStore();
  const days = weekDays(monday);
  const today = todayKey();
  const byDay = new Map(assignments.map((a) => [a.day, a]));
  const total = assignments.reduce((s, a) => s + a.hours, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        className="mb-0"
        title="My shifts"
        description="Your rostered shifts. Contact your manager or HR to change them."
      />

      <Card className="p-4">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <WeekNav monday={monday} onChange={setMonday} />
          <span className="text-sm text-muted-foreground">
            {assignments.length} {assignments.length === 1 ? 'shift' : 'shifts'} · {formatHours(total)}
          </span>
        </div>
        {error ? (
          <p className="py-8 text-center text-sm text-destructive-strong">{error}</p>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-7">
            {days.map((d, i) => {
              const a = byDay.get(d);
              return (
                <div
                  key={d}
                  className={cn('rounded-lg border p-3', d === today ? 'border-primary bg-primary/5' : 'border-border')}
                >
                  <p className="mb-2 text-xs font-medium text-muted-foreground">
                    {WEEKDAYS[i]} {dayHeader(d)}{d === today ? ' · Today' : ''}
                  </p>
                  {isLoading && !a ? (
                    <div className="h-10 animate-pulse rounded-md bg-secondary" />
                  ) : a ? (
                    <>
                      <ShiftChip name={a.shiftName} color={a.color} start={a.startTime} end={a.endTime} />
                      <p className="mt-1.5 text-xs text-muted-foreground">{formatHours(a.hours)}</p>
                    </>
                  ) : (
                    <p className="py-2 text-sm text-muted-foreground">Not scheduled</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <h2 className="mb-3 text-foreground font-display text-card-title">Shift types</h2>
          <ShiftTypeList shifts={shifts} canManage={false} onEdit={() => {}} onDelete={() => {}} />
        </Card>
        <SwapList swaps={swaps} />
      </div>
    </div>
  );
}

function SwapList({ swaps }: { swaps: ReturnType<typeof useShiftStore.getState>['swaps'] }) {
  return (
    <Card className="p-4">
      <h2 className="mb-3 text-foreground font-display text-card-title">Shift swap requests</h2>
      {swaps.length === 0 ? (
        <p className="text-sm text-muted-foreground">No swap requests.</p>
      ) : (
        <ul className="divide-y divide-border">
          {swaps.map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm text-foreground">{s.fromName} ↔ {s.toName}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {s.day ? fromKey(s.day).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) : ''} · {s.shift}
                </p>
              </div>
              <Badge
                variant={s.status === 'approved' ? 'success' : s.status === 'rejected' ? 'destructive' : 'warning'}
                className="capitalize"
              >
                {s.status}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/* ──────────────────────────────── page ───────────────────────────────── */

export function ShiftManagement() {
  const { shifts, assignments, swaps, isLoading, error, fetchShifts, fetchAssignments, fetchSwaps, deleteShift, notifyRoster } = useShiftStore();
  const { fetchAllEmployees } = useEmployeeStore();
  const canManage = useRbacStore((s) => s.hasPermission('Shifts', 'Shift Assignments', 'Create'));

  const [monday, setMonday] = useState(() => startOfWeek(todayKey()));
  const [employees, setEmployees] = useState<RosterEmployee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [typeDialog, setTypeDialog] = useState<{ open: boolean; editing: ShiftType | null }>({ open: false, editing: null });
  const [bulk, setBulk] = useState<{ open: boolean; preselected?: string[] }>({ open: false });
  const [deleting, setDeleting] = useState<ShiftType | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const days = weekDays(monday);
  const today = todayKey();

  useEffect(() => {
    fetchShifts();
    fetchSwaps();
  }, [fetchShifts, fetchSwaps]);

  useEffect(() => {
    fetchAssignments(days[0], days[6]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monday, fetchAssignments]);

  useEffect(() => {
    if (!canManage) return;
    let active = true;
    setEmployeesLoading(true);
    fetchAllEmployees()
      .then((rows) => {
        if (!active) return;
        setEmployees(rows
          .filter((e) => e.status !== 'terminated' && e.status !== 'resigned')
          .map((e) => ({ id: e.id, name: e.displayName, department: e.department || '' }))
          .sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => active && toast.error('Could not load employees'))
      .finally(() => active && setEmployeesLoading(false));
    return () => { active = false; };
  }, [canManage, fetchAllEmployees]);

  /** employeeId → day → assignment for the visible week. */
  const grid = useMemo(() => {
    const m = new Map<string, Map<string, ShiftAssignment>>();
    for (const a of assignments) {
      if (!m.has(a.employeeId)) m.set(a.employeeId, new Map());
      m.get(a.employeeId)!.set(a.day, a);
    }
    return m;
  }, [assignments]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? employees.filter((e) => `${e.name} ${e.department}`.toLowerCase().includes(q)) : employees;
  }, [employees, query]);

  if (!canManage) return <MyShifts monday={monday} setMonday={setMonday} />;

  const weekHours = assignments.reduce((s, a) => s + a.hours, 0);
  const scheduledPeople = grid.size;

  const sendSchedule = async () => {
    setSending(true);
    try {
      const r = await notifyRoster(days[0], days[6]);
      toast.success(r.employees === 0
        ? 'No one is scheduled this week to notify.'
        : `Schedule sent to ${r.employees} employee(s)${r.emailed ? ' · ' + r.emailed + ' email(s)' : ''}.`);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await deleteShift(deleting.id);
      toast.success(`${deleting.name} deleted`);
      setDeleting(null);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        className="mb-0"
        title="Shifts"
        description="Plan who works when. Click a day to set or clear one shift, or assign many at once."
        actions={
          <>
            <Button variant="secondary" onClick={() => setTypeDialog({ open: true, editing: null })}>
              <Plus /> New shift type
            </Button>
            <Button onClick={() => setBulk({ open: true })} disabled={shifts.length === 0}>
              <CalendarPlus /> Assign shifts
            </Button>
          </>
        }
      />

      <Card className="p-4">
        <h2 className="mb-3 text-foreground font-display text-card-title">Shift types</h2>
        <ShiftTypeList
          shifts={shifts}
          canManage
          onEdit={(s) => setTypeDialog({ open: true, editing: s })}
          onDelete={setDeleting}
        />
      </Card>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-foreground font-display text-card-title">Roster</h2>
            <WeekNav monday={monday} onChange={setMonday} />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-muted-foreground">
              {assignments.length} {assignments.length === 1 ? 'shift' : 'shifts'} · {scheduledPeople} {scheduledPeople === 1 ? 'person' : 'people'} · {formatHours(weekHours)}
            </span>
            <Button variant="secondary" size="sm" onClick={sendSchedule} isLoading={sending} disabled={assignments.length === 0}
              title="Email this week's schedule to everyone rostered, and notify them in the app">
              <Mail /> Send schedule email
            </Button>
            <div className="w-full sm:w-64">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search employees"
                leftIcon={<Search className="size-4 text-muted-foreground" />}
              />
            </div>
          </div>
        </div>

        {error && <p className="border-b border-border px-4 py-2 text-sm text-destructive-strong">{error}</p>}

        {shifts.length === 0 ? (
          <EmptyState
            className="m-4"
            icon={<CalendarClock />}
            title="Create a shift type first"
            description="Shift types define the hours people work. Once you have one, you can build the roster."
            action={<Button onClick={() => setTypeDialog({ open: true, editing: null })}><Plus /> New shift type</Button>}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] table-fixed text-sm">
              <colgroup>
                <col className="w-56" />
                {days.map((d) => <col key={d} />)}
                <col className="w-24" />
              </colgroup>
              <thead>
                <tr className="border-b border-border bg-secondary/50 text-xs text-muted-foreground">
                  <th className="sticky left-0 z-10 bg-secondary px-4 py-2 text-left font-semibold">Employee</th>
                  {days.map((d, i) => (
                    <th key={d} className={cn('px-1 py-2 text-center font-semibold', d === today && 'bg-primary/10 text-primary-strong')}>
                      {WEEKDAYS[i]} {dayHeader(d)}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-right font-semibold">Hours</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {employeesLoading && employees.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">
                    <Loader2 className="mr-2 inline size-4 animate-spin" /> Loading employees…
                  </td></tr>
                ) : visible.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">
                    {query ? 'No employees match your search.' : 'No active employees yet.'}
                  </td></tr>
                ) : visible.map((emp) => {
                  const row = grid.get(emp.id);
                  const hours = row ? [...row.values()].reduce((s, a) => s + a.hours, 0) : 0;
                  const overtime = hours > STANDARD_WEEKLY_HOURS;
                  return (
                    <tr key={emp.id} className="hover:bg-secondary/30">
                      <td className="sticky left-0 z-10 bg-card px-4 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate font-medium text-foreground">{emp.name}</p>
                            <p className="truncate text-xs text-muted-foreground">{emp.department || 'No department'}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="size-7 shrink-0"
                            aria-label={`Assign shifts to ${emp.name}`}
                            onClick={() => setBulk({ open: true, preselected: [emp.id] })}
                          >
                            <CalendarPlus className="size-3.5" />
                          </Button>
                        </div>
                      </td>
                      {days.map((d) => (
                        <RosterCell
                          key={d}
                          employeeId={emp.id}
                          day={d}
                          assignment={row?.get(d)}
                          shifts={shifts}
                          isToday={d === today}
                        />
                      ))}
                      <td className="px-3 py-2 text-right tabular-nums">
                        {hours ? (
                          <span className={cn(overtime ? 'font-semibold text-warning-strong' : 'text-foreground')}
                            title={overtime ? `Above ${STANDARD_WEEKLY_HOURS}h this week` : undefined}>
                            {formatHours(hours)}
                          </span>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {isLoading && assignments.length > 0 && (
          <p className="border-t border-border px-4 py-2 text-xs text-muted-foreground">Refreshing…</p>
        )}
      </Card>

      <SwapList swaps={swaps} />

      <ShiftTypeDialog
        open={typeDialog.open}
        editing={typeDialog.editing}
        onOpenChange={(open) => setTypeDialog((s) => ({ ...s, open }))}
      />

      <BulkAssignDialog
        open={bulk.open}
        onOpenChange={(open) => setBulk((s) => ({ ...s, open }))}
        employees={employees}
        defaultFrom={days[0]}
        defaultTo={days[6]}
        preselected={bulk.preselected}
      />

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete {deleting?.name}?</DialogTitle>
            <DialogDescription>
              {deleting && deleting.upcomingAssignments > 0
                ? `${deleting.upcomingAssignments} upcoming ${deleting.upcomingAssignments === 1 ? 'shift uses' : 'shifts use'} this type. Reassign or clear them on the roster before deleting it.`
                : 'This shift type will be removed. Past roster days keep their recorded hours.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleting(null)} disabled={deleteBusy}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              isLoading={deleteBusy}
              disabled={!!deleting && deleting.upcomingAssignments > 0}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
